import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HandPlatter } from "lucide-react";
import { api, USER_ID, track } from "./api";

const CUSTOMER_STATUS = {
  waiting_for_waiter: { text: "Sent to your VeerGi for confirmation", color: "#F0B429" },
  waiter_reviewing: { text: "Your VeerGi is reviewing your order", color: "#F0B429" },
  confirmed: { text: "Confirmed by your VeerGi", color: "#5AB0FF" },
  pos_failed: { text: "Your VeerGi is confirming your order", color: "#F0B429" },
  sent_to_pos: { text: "Confirmed — sent to the kitchen", color: "#5AB0FF" },
  preparing: { text: "Being prepared fresh", color: "#5AB0FF" },
  ready: { text: "Ready — coming to your table!", color: "#2ECC71" },
  served: { text: "Served — enjoy!", color: "#3FD8C2" },
  payment_requested: { text: "Your VeerGi will bring the payment reader", color: "#D4AF37" },
  paid: { text: "Paid — thank you!", color: "#2ECC71" },
};

const rid = () => `di-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

const DrawerTableChip = ({ active, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const current = active ? active.table : (localStorage.getItem("xp_table") || "");
  const save = async () => {
    const val = draft.trim().slice(0, 6);
    if (!val || saving) return;
    if (active) {
      setSaving(true);
      try {
        const r = await api.post("/dinein/update-table", { user_id: USER_ID, table: val });
        localStorage.setItem("xp_table", val);
        window.dispatchEvent(new Event("xp-table-changed"));
        track("dinein_table_chip_changed", { order_id: r.data.order_id, table: r.data.table });
        onChanged?.(r.data.table);
        toast.success(`Table updated to ${r.data.table} — your VeerGi knows where to find you`);
      } catch (e) {
        toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not change the table — please tell your VeerGi");
        setSaving(false);
        return;
      }
      setSaving(false);
    } else {
      localStorage.setItem("xp_table", val);
      window.dispatchEvent(new Event("xp-table-changed"));
      track("table_chip_changed", { table: val });
    }
    setEditing(false);
  };
  if (!current) return null;
  if (editing) {
    return (
      <div data-testid="drawer-table-edit" className="mt-2.5 flex gap-2 items-center">
        <input
          data-testid="drawer-table-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 6))}
          placeholder="Table #"
          autoFocus
          className="w-24 bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-2.5 text-sm text-center font-bold focus:outline-none focus:border-[#D4AF37]/70"
        />
        <button data-testid="drawer-table-save" onClick={save} disabled={!draft.trim() || saving}
          className="flex-1 bg-[#D4AF37] text-black font-bold rounded-full py-2.5 text-[10px] tracking-widest disabled:opacity-50">
          {saving ? "SAVING…" : "SAVE TABLE"}
        </button>
        <button data-testid="drawer-table-cancel" onClick={() => setEditing(false)} aria-label="Cancel"
          className="shrink-0 w-9 h-9 rounded-full border border-white/20 text-white/60 text-sm">✕</button>
      </div>
    );
  }
  return (
    <button
      data-testid="drawer-table-chip"
      onClick={() => { setDraft(current); setEditing(true); }}
      className="mt-2.5 w-full flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 py-2.5 px-4"
    >
      <HandPlatter size={12} className="text-[#D4AF37]" />
      <span className="text-[11px] font-black tracking-widest text-[#D4AF37]">TABLE {current}</span>
      <span className="text-[9px] font-bold tracking-widest text-white/40">· TAP TO CHANGE</span>
    </button>
  );
};

export const DineInPanel = ({ orderable, onSent }) => {
  const [open, setOpen] = useState(false);
  const [table, setTable] = useState(() => localStorage.getItem("xp_table") || "");
  const [guests, setGuests] = useState("");
  const [allergies, setAllergies] = useState("");
  const [instructions, setInstructions] = useState("");
  const [idem, setIdem] = useState(rid);
  const [sending, setSending] = useState(false);
  const [active, setActive] = useState(null);
  const [call, setCall] = useState(null);

  useEffect(() => {
    const syncTable = () => setTable(localStorage.getItem("xp_table") || "");
    window.addEventListener("xp-table-changed", syncTable);
    return () => window.removeEventListener("xp-table-changed", syncTable);
  }, []);

  useEffect(() => {
    const load = () => api.get(`/dinein/status?user_id=${USER_ID}`).then((r) => {
      const o = r.data.order;
      setActive(o && !["completed", "cancelled"].includes(o.status) ? o : null);
      setCall(r.data.call || null);
    }).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const callWaiter = async (tbl) => {
    try {
      const r = await api.post("/dinein/call", { user_id: USER_ID, table: tbl });
      setCall({ call_id: r.data.call_id, status: "open", table: tbl });
      track("waiter_call_tap", { table: tbl });
      if (r.data.duplicate) toast("Your VeerGi has already been called — hang tight");
    } catch {
      toast.error("Couldn't reach the team — please flag your VeerGi");
    }
  };

  const CallRow = ({ tbl }) => (
    <div className="mt-2.5">
      {call && (
        <p data-testid="call-state" className="text-xs font-bold text-[#F0B429] mb-1.5">
          {call.status === "acked" ? `✓ ${call.acked_by || "Your VeerGi"} is on the way!` : "✓ VeerGi called — hang tight"}
        </p>
      )}
      {(!call || call.status === "acked") && (
        <button data-testid="dinein-call" onClick={() => callWaiter(tbl)}
          className="w-full border border-white/20 text-white/75 font-bold rounded-full py-2.5 text-[10px] tracking-widest hover:bg-white/5">
          🖐 CALL MY VEERGI
        </button>
      )}
    </div>
  );

  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      const r = await api.post("/dinein/submit", {
        user_id: USER_ID, table: table.trim(), idempotency_key: idem,
        guests: guests ? Number(guests) : null,
        allergies: allergies.trim() || null, special_instructions: instructions.trim() || null,
        items: orderable.map(({ slug, name, price, qty }) => ({ slug, name, price, qty })),
      });
      track("dinein_sent", { order_id: r.data.order_id, table: table.trim() });
      localStorage.setItem("xp_table", table.trim());
      window.dispatchEvent(new Event("xp-table-changed"));
      if (!r.data.duplicate) onSent?.(orderable.map((p) => p.slug));
      setActive({ order_id: r.data.order_id, status: r.data.status, table: table.trim(), rounds_count: 1 });
      setOpen(false);
      setIdem(rid());
      if (r.data.duplicate) toast("Your order was already sent — your VeerGi has it");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not send — please try again");
    }
    setSending(false);
  };

  const sendRound = async () => {
    if (sending) return;
    setSending(true);
    try {
      const r = await api.post("/dinein/add-round", {
        user_id: USER_ID, idempotency_key: idem,
        items: orderable.map(({ slug, name, price, qty }) => ({ slug, name, price, qty })),
      });
      track("dinein_round_sent", { order_id: r.data.order_id, round: r.data.round });
      if (!r.data.duplicate) onSent?.(orderable.map((p) => p.slug));
      setIdem(rid());
      if (r.data.new_order) {
        setActive((a) => ({ order_id: r.data.order_id, status: r.data.status, table: a?.table, rounds_count: 1, new_round_pending: false }));
        toast.success("Your earlier order is closed out — this went in as a fresh order for your table");
      } else {
        setActive((a) => ({ ...a, rounds_count: r.data.round, new_round_pending: true }));
        toast.success(`Round ${r.data.round} sent — your VeerGi will confirm the new items`);
      }
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not send — please try again");
    }
    setSending(false);
  };

  if (active) {
    const st = CUSTOMER_STATUS[active.status] || CUSTOMER_STATUS.waiting_for_waiter;
    return (
      <div data-testid="dinein-status-panel" className="mt-4 rounded-2xl border p-4" style={{ borderColor: `${st.color}66`, background: `${st.color}12` }}>
        <p className="text-[10px] tracking-[0.25em] font-black" style={{ color: st.color }}>DINE-IN · TABLE {active.table} · {active.order_id}</p>
        <p data-testid="dinein-status-text" className="text-sm font-bold mt-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full animate-pulse shrink-0" style={{ background: st.color }} />
          {st.text}
        </p>
        {active.new_round_pending && (
          <p data-testid="dinein-round-pending" className="text-xs font-bold text-[#F0B429] mt-2">
            Round {active.rounds_count} sent — waiting for your VeerGi to confirm the new items
          </p>
        )}
        <p className="text-[10px] text-white/40 mt-1.5">Your order has been sent to your VeerGi for confirmation — nothing goes to the kitchen until they confirm it with you.</p>
        <DrawerTableChip active={active} onChanged={(t) => setActive((a) => ({ ...a, table: t }))} />
        {orderable.length > 0 && (
          <button data-testid="dinein-send-round" onClick={sendRound} disabled={sending}
            className="mt-3 w-full border border-[#F0B429]/60 text-[#F0B429] font-bold rounded-full py-3.5 text-xs tracking-widest hover:bg-[#F0B429]/10">
            {sending ? "SENDING…" : "SEND THESE TO MY VEERGI"}
          </button>
        )}
        <CallRow tbl={active.table} />
      </div>
    );
  }

  if (orderable.length === 0) return null;

  return (
    <div data-testid="dinein-panel" className="mt-4 rounded-2xl border border-[#F0B429]/35 bg-[#171307] p-4">
      <p className="text-[10px] tracking-[0.25em] text-[#F0B429] font-black flex items-center gap-1.5"><HandPlatter size={13} /> DINING IN? SEND IT TO YOUR VEERGI</p>
      {!open ? (
        <>
          <button data-testid="dinein-open" onClick={() => setOpen(true)}
            className="mt-3 w-full border border-[#F0B429]/60 text-[#F0B429] font-bold rounded-full py-3.5 text-xs tracking-widest hover:bg-[#F0B429]/10">
            SEND TO YOUR VEERGI — THEY CONFIRM AT YOUR TABLE
          </button>
          <DrawerTableChip />
        </>
      ) : (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            <input data-testid="dinein-table" value={table} onChange={(e) => setTable(e.target.value)} placeholder="Table #"
              className="w-24 bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-3 text-sm text-center focus:outline-none focus:border-[#F0B429]/70" />
            <input data-testid="dinein-guests" value={guests} onChange={(e) => setGuests(e.target.value.replace(/\D/g, "").slice(0, 2))} placeholder="How many guests?"
              inputMode="numeric" pattern="[0-9]*" className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#F0B429]/70" />
          </div>
          <input data-testid="dinein-allergies" value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Any allergies? (very important)"
            className="w-full bg-[#1A1A1A] border border-[#FF5A4E]/40 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#FF5A4E]" />
          <input data-testid="dinein-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Special instructions (optional)"
            className="w-full bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#F0B429]/70" />
          <button data-testid="dinein-send" onClick={send} disabled={sending || !table.trim()}
            className="w-full bg-[#F0B429] text-black font-bold rounded-full py-3.5 text-xs tracking-widest disabled:opacity-50">
            {sending ? "SENDING…" : "SEND TO VEERGI"}
          </button>
          <p className="text-[10px] text-white/40 text-center">Your VeerGi confirms everything with you before it goes to the kitchen.</p>
          {table.trim() && <CallRow tbl={table.trim()} />}
        </div>
      )}
    </div>
  );
};
